/**
 * multi-variant-quantity.js
 *
 * Renders ONLY the per-size quantity table. Colour swatches now live in
 * <multi-variant-colour> upstream of the design-placement block. This
 * element listens for 'mvq:colour-changed' to know which colour to filter
 * variants by.
 *
 * Communication with the ATC element (multi-variant-atc):
 *   - Listens for 'mvq:request-state'
 *   - Responds by dispatching 'mvq:state' with current quantities + helpers
 *
 * Design fee mechanic:
 *   - `Embroidery Area sq in` is written as a hidden input by design-placement-block
 *   - getDesignAreaSqIn() reads it from the form / placement inputs
 *   - This integer is passed to multi-variant-atc as `designAreaSqIn`
 *   - ATC multiplies by total garment qty to get fee product qty
 */

(function () {
  'use strict';

  if (customElements.get('multi-variant-quantity')) return;

  // ── Helpers ────────────────────────────────────────────────────────────────

  function parseDataJSON(raw) {
    try {
      const ta = document.createElement('textarea');
      ta.innerHTML = raw || '';
      return JSON.parse(ta.value);
    } catch (e) { return null; }
  }

  function formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, '£{{amount}}');
    }
    return '£' + (cents / 100).toFixed(2);
  }

  // ── Bundle arithmetic ──────────────────────────────────────────────────────
  //
  // A bundle product (product.metafields.custom.bundle_size > 0) must be added
  // as EXACTLY that many items across sizes. bundleSize 0 means "not a bundle",
  // and every caller below must leave such products completely alone.
  //
  // Pure and unit-tested in tests/multi-variant-bundle.test.mjs.

  function bundleProgress(quantities, bundleSize) {
    const size  = parseInt(bundleSize, 10) || 0;
    const total = Object.values(quantities || {})
      .reduce((sum, q) => sum + (parseInt(q, 10) || 0), 0);
    return {
      total:     total,
      remaining: Math.max(0, size - total),
      // Exact match only — over-selection is as invalid as under-selection.
      complete:  size > 0 && total === size,
    };
  }

  // The ceiling for one size's stepper: stock, further limited by what the
  // bundle has left to allocate. A row keeps whatever it already holds, so
  // reaching the target never retroactively invalidates a row.
  function bundleRowMax(stockMax, currentQty, remaining, bundleSize) {
    if (!(parseInt(bundleSize, 10) > 0)) return stockMax;
    const cur = parseInt(currentQty, 10) || 0;
    const rem = parseInt(remaining, 10)  || 0;
    return Math.max(0, Math.min(stockMax, cur + rem));
  }

  // Exported for the node unit tests; a no-op in the browser.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { bundleProgress, bundleRowMax };
  }

  // ── Custom Element ─────────────────────────────────────────────────────────

  class MultiVariantQuantity extends HTMLElement {

    constructor() {
      super();
      this.quantities     = {};
      this.selectedColour = null;
      this.variants       = [];
      this.options        = [];
      this.colourIdx      = -1;
      this.sizeIdx        = -1;
    }

    connectedCallback() {
      this.colourOptionName = this.dataset.colourOption || 'Colour';
      this.sizeOptionName   = this.dataset.sizeOption   || 'Size';
      this.invThreshold     = parseInt(this.dataset.inventoryThreshold) || 10;
      this.showPrice        = this.dataset.showPricePerRow !== 'false';
      this.bundleSize       = parseInt(this.dataset.bundleSize, 10) || 0;

      // A bundle is sold at one fixed price (unit price x bundle size), shown
      // in the price block. A per-size price column would contradict it, so
      // reuse the existing showPrice switch rather than branching the renderer.
      if (this.bundleSize > 0) this.showPrice = false;

      this.variants = parseDataJSON(this.dataset.variants) || [];
      this.options  = parseDataJSON(this.dataset.options)  || [];

      const nameLower = n => (n || '').trim().toLowerCase();
      const findOptionIdx = (aliases) => {
        for (const alias of aliases) {
          const idx = this.options.findIndex(o => nameLower(o.name) === nameLower(alias));
          if (idx >= 0) return idx;
        }
        return -1;
      };

      this.colourIdx = findOptionIdx([this.colourOptionName, 'Colour', 'Color']);
      this.sizeIdx   = findOptionIdx([this.sizeOptionName,   'Size']);

      this.sizeTable   = this.querySelector('[data-size-table]');
      this.summaryEl   = this.querySelector('[data-summary]');
      this.summaryText = this.querySelector('[data-summary-text]');

      if (this.showPrice) this.sizeTable.classList.add('mvq__size-table--with-price');

      document.addEventListener('mvq:colour-changed', e => {
        this.adoptColourState(e.detail);
        this.quantities = {};
        this.buildSizeTable(this.selectedColour);
      });

      const colourEl = document.querySelector('multi-variant-colour');
      if (colourEl?.selectedColour != null) {
        this.adoptColourState({
          colour:    colourEl.selectedColour,
          variants:  colourEl.variants,
          options:   colourEl.options,
          colourIdx: colourEl.colourIdx,
        });
        this.buildSizeTable(this.selectedColour);
      } else if (colourEl) {
        document.dispatchEvent(new CustomEvent('mvq:request-colour'));
      } else {
        this.selectedColour = this.uniqueColours()[0] || null;
        this.buildSizeTable(this.selectedColour);
      }

      this.topUpVariants();

      document.addEventListener('mvq:request-state', () => this.broadcastState());
      document.addEventListener('mvq:reset', () => {
        this.quantities = {};
        this.buildSizeTable(this.selectedColour);
      });
    }

    adoptColourState(detail) {
      if (!detail) return;
      this.selectedColour = detail.colour ?? this.selectedColour;
      if (Array.isArray(detail.variants) && detail.variants.length > this.variants.length) {
        this.variants  = detail.variants;
        this.options   = detail.options || this.options;
        this.colourIdx = detail.colourIdx ?? this.colourIdx;
      }
    }

    async topUpVariants() {
      const handle = this.dataset.productHandle;
      let product;
      try {
        const res = await fetch(`/products/${handle}.js?variant_limit=1000`);
        if (!res.ok) return;
        product = await res.json();
      } catch (e) {
        console.error('[mvq] variant top-up failed', e);
        return;
      }

      if (!product?.variants || product.variants.length <= this.variants.length) return;

      this.variants = product.variants.map(v => ({
        id:                      v.id,
        title:                   v.title,
        price:                   v.price,
        compare_at_price:        v.compare_at_price,
        available:               v.available,
        inventory_quantity:      v.inventory_quantity,
        inventory_management:    v.inventory_management,
        inventory_policy:        v.inventory_policy,
        options:                 v.options,
        featured_media_id:       v.featured_media?.id ?? null,
        featured_media_position: v.featured_media?.position ?? null,
      }));

      const rebuildValues = (idx) => {
        if (idx < 0 || !this.options[idx]) return;
        const seen = new Set();
        const ordered = [];
        (this.options[idx].values || []).forEach(val => {
          if (!seen.has(val)) { seen.add(val); ordered.push(val); }
        });
        this.variants.forEach(v => {
          const val = v.options?.[idx];
          if (val && !seen.has(val)) { seen.add(val); ordered.push(val); }
        });
        this.options[idx].values = ordered;
      };
      rebuildValues(this.colourIdx);
      rebuildValues(this.sizeIdx);

      this.buildSizeTable(this.selectedColour);
    }

    // ── State broadcast (for ATC) ─────────────────────────────────────────────

    broadcastState() {
      // IMPORTANT: validateDesign() MUST be called first.
      // It calls dp.validate() → writePropertiesToForm(), which appends
      // hidden inputs to the form. readDesignProps() then reads those inputs.
      // Reversing the order leaves designProps empty every time.
      const designValid    = this.validateDesign();
      const designProps    = this.readDesignProps();
      const designAreaSqIn = this.getDesignAreaSqIn();

      document.dispatchEvent(new CustomEvent('mvq:state', {
        detail: {
          quantities:      this.quantities,
          variants:        this.variants,
          bundleSize:      this.bundleSize,
          selectedColour:  this.selectedColour,
          sizeOptionIdx:   this.sizeIdx,
          designProps,
          designAreaSqIn,  // integer sq in per garment — replaces designFee
          designValid,
        },
      }));
    }

    // ── Design placement integration ──────────────────────────────────────────

    getDesignManager() {
      return document.querySelector('design-placement-manager-designplacement');
    }

    validateDesign() {
      const dp = this.getDesignManager();
      if (!dp) return true;
      return dp.validate();
    }

    readDesignProps() {
      const formId = this.dataset.productFormId;
      const form   = document.getElementById(formId);
      const props  = {};

      if (form) {
        Array.from(form.elements).forEach(el => {
          if (!el.name) return;
          const m = el.name.match(/^properties\[(.+)\]$/);
          if (m && el.value !== '') props[m[1]] = el.value;
        });
      }

      document.querySelectorAll('[data-placement-input]').forEach(el => {
        if (!el.name) return;
        const m = el.name.match(/^properties\[(.+)\]$/);
        if (m && el.value !== '' && !(m[1] in props)) props[m[1]] = el.value;
      });

      if (formId) {
        try {
          document.querySelectorAll(`[form="${CSS.escape(formId)}"][name]`).forEach(el => {
            const m = el.name.match(/^properties\[(.+)\]$/);
            if (m && el.value !== '' && !(m[1] in props)) props[m[1]] = el.value;
          });
        } catch (_) {}
      }

      return props;
    }

    // Read the embroidery area in sq in written by writePropertiesToForm().
    // Returns an integer (0 if no embroidery selected).
    getDesignAreaSqIn() {
      // Primary: read from placement inputs written by design-placement-block
      const placementInputs = document.querySelectorAll('[data-placement-input]');
      for (const el of placementInputs) {
        const m = el.name && el.name.match(/^properties\[(.+)\]$/);
        if (m && m[1] === 'Embroidery Area sq in' && el.value) {
          return parseInt(el.value, 10) || 0;
        }
      }

      // Fallback: check the product form directly
      const formId = this.dataset.productFormId;
      const form   = document.getElementById(formId);
      if (form) {
        const el = form.querySelector('input[name="properties[Embroidery Area sq in]"]');
        if (el && el.value) return parseInt(el.value, 10) || 0;
      }

      return 0;
    }

    // ── Variant helpers ───────────────────────────────────────────────────────

    uniqueColours() {
      if (this.colourIdx < 0) return [];
      return this.options[this.colourIdx]?.values || [];
    }

    variantsForColour(colour) {
      if (this.colourIdx < 0 || !colour) return this.variants;
      return this.variants.filter(v => v.options?.[this.colourIdx] === colour);
    }

    // ── Size table ────────────────────────────────────────────────────────────

    buildSizeTable(colour) {
      if (!this.sizeTable) return;

      const variants = this.variantsForColour(colour);
      this.sizeTable.innerHTML = '';
      // Rebuilt with the table: a bundle row's ceiling depends on what every
      // OTHER row currently holds, so each row needs re-checking after any
      // change. refreshStepperLimits() walks this.
      this.rowControls = [];

      const sizeOrder = this.sizeIdx >= 0
        ? (this.options[this.sizeIdx]?.values || [])
        : [];

      const sorted = [...variants].sort((a, b) => {
        const aLabel = a.options?.[this.sizeIdx] ?? a.title;
        const bLabel = b.options?.[this.sizeIdx] ?? b.title;
        const ai = sizeOrder.indexOf(aLabel);
        const bi = sizeOrder.indexOf(bLabel);
        if (ai >= 0 && bi >= 0) return ai - bi;
        if (ai >= 0) return -1;
        if (bi >= 0) return 1;
        return 0;
      });

      // Header row
      const header = document.createElement('div');
      header.className = 'mvq__row mvq__row--header';
      header.setAttribute('aria-hidden', 'true');

      const hSize = document.createElement('span');
      hSize.className   = 'mvq__cell mvq__cell-size';
      hSize.textContent = 'SIZE';

      const hPrice = document.createElement('span');
      hPrice.className   = 'mvq__cell mvq__cell-price';
      hPrice.textContent = 'PRICE';

      const hInv = document.createElement('span');
      hInv.className   = 'mvq__cell mvq__cell-inv';
      hInv.textContent = 'STOCK';

      const hQty = document.createElement('span');
      hQty.className   = 'mvq__cell mvq__cell-qty';
      hQty.textContent = 'QTY';

      header.append(hSize);
      if (this.showPrice) header.append(hPrice);
      header.append(hInv, hQty);
      this.sizeTable.appendChild(header);

      sorted.forEach(variant => this.buildSizeRow(variant));
      this.updateSummary();
    }

    buildSizeRow(variant) {
      const sizeLabel  = variant.options?.[this.sizeIdx] ?? variant.title;
      const available  = this.isAvailable(variant);
      const max        = this.maxQty(variant);
      const currentQty = this.quantities[variant.id] || 0;

      const row = document.createElement('div');
      row.className         = 'mvq__row' + (available ? '' : ' mvq__row--unavailable');
      row.dataset.variantId = variant.id;

      const cellSize = document.createElement('span');
      cellSize.className   = 'mvq__cell mvq__cell-size';
      cellSize.textContent = sizeLabel || variant.title;

      const cellPrice = document.createElement('span');
      cellPrice.className = 'mvq__cell mvq__cell-price';
      if (this.showPrice) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          cellPrice.innerHTML =
            `<s class="mvq__price-was">${formatMoney(variant.compare_at_price)}</s>` +
            `<span>${formatMoney(variant.price)}</span>`;
        } else {
          cellPrice.textContent = formatMoney(variant.price);
        }
      }

      const cellInv = document.createElement('span');
      cellInv.className = 'mvq__cell mvq__cell-inv';
      cellInv.innerHTML = this.inventoryBadge(variant);

      const cellQty = document.createElement('span');
      cellQty.className = 'mvq__cell mvq__cell-qty';
      cellQty.appendChild(this.buildStepper(variant, available, max, currentQty, row, sizeLabel));

      row.append(cellSize);
      if (this.showPrice) row.append(cellPrice);
      row.append(cellInv, cellQty);

      this.sizeTable.appendChild(row);
    }

    buildStepper(variant, available, max, currentQty, row, sizeLabel) {
      const stepper = document.createElement('div');
      stepper.className = 'mvq__stepper';

      const minusBtn = document.createElement('button');
      minusBtn.type      = 'button';
      minusBtn.className = 'mvq__step-btn';
      minusBtn.setAttribute('aria-label', `Decrease ${sizeLabel} quantity`);
      minusBtn.disabled  = !available || currentQty === 0;
      minusBtn.innerHTML = `<svg width="10" height="2" viewBox="0 0 10 2" fill="none" aria-hidden="true">
        <path d="M1 1h8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>`;

      const input = document.createElement('input');
      input.type      = 'number';
      input.className = 'mvq__step-input';
      input.value     = currentQty;
      input.min       = 0;
      if (max < 999) input.max = max;
      input.setAttribute('aria-label', `Quantity for ${sizeLabel}`);
      input.disabled  = !available;

      const plusBtn = document.createElement('button');
      plusBtn.type      = 'button';
      plusBtn.className = 'mvq__step-btn';
      plusBtn.setAttribute('aria-label', `Increase ${sizeLabel} quantity`);
      plusBtn.disabled  = !available || currentQty >= max;
      plusBtn.innerHTML = `<svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
        <path d="M5 1v8M1 5h8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>`;

      stepper.append(minusBtn, input, plusBtn);

      if (available) {
        this.rowControls.push({ variant, row, minusBtn, plusBtn, input });

        // this.maxQty() is re-read on every interaction rather than closing over
        // `max`: on a bundle the ceiling moves as other sizes are selected, so a
        // captured value goes stale the moment another row changes.
        minusBtn.addEventListener('click', () =>
          this.setQty(variant, Math.max(0, (this.quantities[variant.id] || 0) - 1), row, minusBtn, plusBtn, input)
        );
        plusBtn.addEventListener('click', () =>
          this.setQty(variant, Math.min(this.maxQty(variant), (this.quantities[variant.id] || 0) + 1), row, minusBtn, plusBtn, input)
        );
        input.addEventListener('change', () => {
          const v = Math.min(this.maxQty(variant), Math.max(0, parseInt(input.value) || 0));
          this.setQty(variant, v, row, minusBtn, plusBtn, input);
        });
        input.addEventListener('focus', () => { if (input.value === '0') input.value = ''; });
        input.addEventListener('blur',  () => { if (input.value === '') input.value = this.quantities[variant.id] || 0; });
      }

      return stepper;
    }

    setQty(variant, qty, row, minusBtn, plusBtn, input) {
      this.quantities[variant.id] = qty;
      input.value       = qty;
      minusBtn.disabled = qty === 0;
      row.classList.toggle('mvq__row--has-qty', qty > 0);
      // Every row, not just this one: filling a bundle has to stop the others.
      this.refreshStepperLimits();
      this.updateSummary();
    }

    refreshStepperLimits() {
      (this.rowControls || []).forEach(({ variant, plusBtn, input }) => {
        const qty = this.quantities[variant.id] || 0;
        const max = this.maxQty(variant);
        plusBtn.disabled = qty >= max;
        if (max < 999) input.max = max; else input.removeAttribute('max');
      });
    }

    // ── Inventory ─────────────────────────────────────────────────────────────

    isAvailable(variant) {
      if (variant.inventory_management !== 'shopify') return true;
      if (variant.inventory_quantity > 0)              return true;
      if (variant.inventory_policy    === 'continue')  return true;
      return false;
    }

    maxQty(variant) {
      return bundleRowMax(
        this.stockMax(variant),
        this.quantities[variant.id] || 0,
        bundleProgress(this.quantities, this.bundleSize).remaining,
        this.bundleSize,
      );
    }

    stockMax(variant) {
      if (variant.inventory_management !== 'shopify') return 999;
      if (variant.inventory_policy    === 'continue') return 999;
      return Math.max(0, variant.inventory_quantity);
    }

    inventoryBadge(variant) {
      if (variant.inventory_management !== 'shopify') {
        return `<span class="mvq__inv mvq__inv--ok">In stock</span>`;
      }
      const qty    = variant.inventory_quantity;
      const policy = variant.inventory_policy;

      if (qty <= 0) {
        if (policy === 'continue') return `<span class="mvq__inv mvq__inv--ok">Pre-order</span>`;
        return `<span class="mvq__inv mvq__inv--out">Out of stock</span>`;
      }
      if (qty <= this.invThreshold) return `<span class="mvq__inv mvq__inv--low">Low stock</span>`;
      return `<span class="mvq__inv mvq__inv--ok">In stock</span>`;
    }

    // ── Summary ───────────────────────────────────────────────────────────────

    updateSummary() {
      const entries  = Object.entries(this.quantities).filter(([, q]) => q > 0);
      const totalQty = entries.reduce((s, [, q]) => s + q, 0);
      const bundle   = bundleProgress(this.quantities, this.bundleSize);

      const announce = () => document.dispatchEvent(new CustomEvent('mvq:qty-changed', {
        detail: {
          totalQty:   totalQty,
          bundleSize: this.bundleSize,
          remaining:  bundle.remaining,
          complete:   bundle.complete,
        },
      }));

      // A bundle costs unit price x bundle size whatever the customer picks, so
      // a running money total would be wrong. Show how far off the target they
      // are instead — the same number the Add-to-Cart gate acts on.
      if (this.bundleSize > 0) {
        this.summaryText.textContent = bundle.complete
          ? `Bundle complete — ${this.bundleSize} of ${this.bundleSize} items selected`
          : `Select ${bundle.remaining} more ${bundle.remaining === 1 ? 'item' : 'items'} — ${totalQty} of ${this.bundleSize} selected`;
        this.summaryEl.style.display = '';
        announce();
        return;
      }

      if (totalQty === 0) {
        this.summaryEl.style.display = 'none';
        announce();
        return;
      }

      const totalPrice = entries.reduce((s, [id, qty]) => {
        const v = this.variants.find(v => v.id === parseInt(id));
        return s + (v ? v.price * qty : 0);
      }, 0);

      const word = totalQty === 1 ? 'item' : 'items';
      this.summaryText.textContent = `${totalQty} ${word} — ${formatMoney(totalPrice)}`;
      this.summaryEl.style.display = '';

      announce();
    }
  }

  customElements.define('multi-variant-quantity', MultiVariantQuantity);
})();
